import { z } from 'zod';
// frequent-customer-memberships · Slice S2a — Member & Vehicle Identity
// (spec Capability 2). Org-scoped member identity + 1..N registered plates,
// org-admin only. Plate normalization (trim/uppercase/strip whitespace)
// happens server-side via the `Plate` VO (tickets bounded context, reused
// per design §1) — the schema here only enforces "non-empty string",
// mirroring how tariff/zone schemas defer money/plate formatting details to
// the domain layer.
export const PlateInputSchema = z.string().trim().min(1, 'Plate cannot be empty').max(20);
export const CreateMemberRequestSchema = z.object({
    name: z.string().trim().min(1, 'Name is required').max(160),
    email: z.string().trim().email('A valid email is required'),
    phone: z.string().trim().min(1).max(40).optional(),
    plates: z
        .array(PlateInputSchema)
        .min(1, 'At least one plate is required')
        .max(10, 'A member may register at most 10 plates at once'),
});
export const ReassignMemberVehicleRequestSchema = z.object({
    plate: PlateInputSchema,
    toMemberId: z.string().uuid('toMemberId must be a valid UUID'),
});
export const MemberVehicleResponseSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    memberId: z.string().uuid(),
    plate: z.string(),
    createdAt: z.string(),
});
// The member's current (non-terminal) membership, flattened with its plan
// name for the admin members listing. `canceled` never appears here — it is
// terminal, so a canceled-only member reports `activeMembership: null`.
export const MemberActiveMembershipSchema = z.object({
    membershipId: z.string().uuid(),
    planId: z.string().uuid(),
    planName: z.string(),
    status: z.enum(['pending_activation', 'active', 'past_due', 'suspended']),
});
export const MemberResponseSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    name: z.string(),
    email: z.string(),
    phone: z.string().nullable(),
    stripeCustomerId: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    vehicles: z.array(MemberVehicleResponseSchema),
    activeMembership: MemberActiveMembershipSchema.nullable(),
});
export const ListMembersResponseSchema = z.object({
    members: z.array(MemberResponseSchema),
});
// frequent-customer-memberships · Slice S2b-BE — admin-initiated enrollment
// (spec Capability 3 "Enrollment is admin-initiated only"). The visitor-facing
// OTP/SetupIntent request schemas are kept backend-internal for this slice
// (see S2b-BE apply-progress) — S2b-FE owns the canonical shared schemas for
// those endpoints.
export const IssueEnrollmentRequestSchema = z.object({
    planId: z.string().uuid('planId must be a valid UUID'),
});
export const IssueEnrollmentResponseSchema = z.object({
    membershipId: z.string().uuid(),
    memberId: z.string().uuid(),
    planId: z.string().uuid(),
    status: z.string(),
    createdAt: z.string(),
    // Admin-UX extension: the same HMAC enrollment link that was emailed to
    // the member, also returned to the org admin who initiated the enrollment
    // so the admin can copy/share it directly (e.g. for staging where the
    // `log` mailer only writes to server logs).
    enrollmentUrl: z.string().url(),
});
//# sourceMappingURL=member-schemas.js.map